// AN/AVS-9 night-vision-goggle effect — single-monocle phosphor look.
//
// Runs as the final composer pass on the tone-mapped image (so the green tint
// and black lens edge stay predictable). The phosphor disc is sampled with a
// radius-dependent disc blur — sharp at the center, increasingly soft toward the
// rim — to mimic the optic's field curvature / depth-of-field falloff. Plus a
// hash-based grain and a couple of smoothsteps for the lens edge.

uniform sampler2D tDiffuse;
uniform float     uTime;    // advanced by dt each frame — animates the grain
uniform float     uAspect;  // viewport w/h — keeps the lens circular
uniform float     uZoom;    // optic magnification (1 = none) — grows the disc when the cockpit view zooms
uniform vec2      uDiscOffset; // UV shift of the optic centre — animates the disc down from the top (+y) with a bit of shake

varying vec2 vUv;

// Image gain — brightens the dark night scene into the visible phosphor range.
const float GAIN = 5.0;
// Green P43-phosphor tint.
const vec3  PHOSPHOR = vec3(0.10, 1.0, 0.28);
// Max disc-blur kernel radius (UV units, screen-space) reached at the rim.
const float BLUR_MAX = 0.004;

// Two-ring, 13-tap disc blur centered on `uv`. `blur` is the screen-space kernel
// radius; offsets divide x by aspect so the kernel stays circular on screen.
vec3 discBlur(vec2 uv, float blur) {
  if (blur < 1e-4) return texture2D(tDiffuse, uv).rgb;
  vec3 sum = texture2D(tDiffuse, uv).rgb; // center tap
  for (int i = 0; i < 6; i++) {
    float a = float(i) * 1.0471975512;            // 60° steps
    vec2 d1 = vec2(cos(a), sin(a));
    vec2 d2 = vec2(cos(a + 0.5235987756),         // inner ring, rotated 30°
                   sin(a + 0.5235987756));
    sum += texture2D(tDiffuse, uv + vec2(d2.x / uAspect, d2.y) * blur * 0.5).rgb;
    sum += texture2D(tDiffuse, uv + vec2(d1.x / uAspect, d1.y) * blur).rgb;
  }
  return sum / 13.0;
}

// Cheap 3-component hash → [0,1] ("hash without sine", Dave Hoskins). The extra
// channel and the additive-then-multiplicative combine avoid the hyperbolic
// banding that the 2-component `fract(x*y)` variant streaks into. Takes a vec3 so
// time can ride along as its own decorrelated axis rather than a uniform offset.
float hash(vec3 p) {
  vec3 p3 = fract(p * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

void main() {
  // Aspect-corrected radius: 0 at center, ~1 at the top/bottom screen edge.
  // `uDiscOffset` slides the optic centre (the green disc, its vignette and the
  // black lens housing all ride it) so the goggle can swing down from the top of
  // the FOV and rattle into place. The scene itself is sampled at the unshifted
  // vUv below — only the lens moves, the world behind it stays put.
  vec2 c = vUv - 0.5 - uDiscOffset;
  c.x *= uAspect;
  // Divide by the optic magnification so the phosphor disc, vignette and lens
  // edge all grow together as the cockpit zoom narrows the FOV — the goggle
  // view zooms in lockstep with the scene rather than staying a fixed circle.
  float r = length(c) * 1.8 / uZoom;

  vec3 src = texture2D(tDiffuse, vUv).rgb;

  // Radius-dependent disc blur: crisp through the center, softening toward the
  // rim. Ramps with r² (gentle start, steep at the edge) and only inside the
  // phosphor disc, so the untouched outer scene stays sharp.
  float blur = BLUR_MAX * smoothstep(0.10, 0.80, r);
  blur *= blur / max(BLUR_MAX, 1e-4); // r²-style curve — keep peak at BLUR_MAX
  vec3 phos = discBlur(vUv, blur);

  // Monochrome luminance → gain → green phosphor.
  float luma = dot(phos, vec3(0.299, 0.587, 0.114));
  luma = pow(max(luma * GAIN, 0.0), 0.85);
  vec3 col = PHOSPHOR * luma;

  // Sensor scintillation: fine grain on isotropic, near-integer cell coords
  // (square cells via aspect correction), stepped at ~24 Hz so it shimmers
  // without strobing. Time rides the z axis so each step is a fresh field.
  vec2 cell = floor(vec2(vUv.x * uAspect, vUv.y) * 700.0);
  float tStep = floor(uTime * 24.0);
  float n = hash(vec3(cell, tStep));
  col += (n - 0.5) * 0.01;

  // Hot-pixel scintillation: a tiny fraction of cells blow out to a bright
  // phosphor spike each field. A finer, decorrelated cell grid (offset + time)
  // thresholds so only a sparse few pixels fire, smoothstepped into a brief flare.
  vec2 sparkCell = floor(vec2(vUv.x * uAspect, vUv.y) * 1400.0);
  float spark = hash(vec3(sparkCell + 17.0, tStep * 1.7 + 3.0));
  col += PHOSPHOR * smoothstep(0.9992, 1.0, spark) * 0.6;

  // Faint phosphor noise floor so deep shadows still glow rather than read pure
  // black (NVGs never show true black).
  col += vec3(0.0, 0.02, 0.005);

  // Inner vignette — gentle darkening toward the rim.
  col *= 1.0 - 0.32 * r * r;

  // ── Composite: NVG disc → blurry black ring → untouched outer scene ─────────
  // Two feathered weights along the radius. `inside` is the high-exposure
  // phosphor disc; `outside` is the original, unmodified image. Whatever radius
  // band neither weight claims (between the two feathers) stays pure black — the
  // blurry ring of the optic housing. Both feathers are wide so the disc edge and
  // the ring's outer edge read as the lens's out-of-focus depth-of-field blur.
  float inside  = 1.0 - smoothstep(0.55, 0.82, r); // green phosphor disc
  float outside = smoothstep(0.92, 1.18, r);        // untouched scene

  vec3 outCol = col * inside + src * outside;
  gl_FragColor = vec4(outCol, 1.0);
}
